import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Spinner } from 'react-bootstrap';
import { INK } from 'Utils/styleTokens';
import SchemaIdentityCard from './SchemaIdentityCard';
import { sortIdentities } from './constants';

/**
 * Fallback status card for a profile run whose source has no identity card yet
 * (first-ever profile — no snapshot until the run finishes). Runs against sources
 * that already have an identity render INSIDE that identity's card instead.
 */
const RunningJobCard = ({ job, onCancel, cancelling }) => (
  <div
    className='metadata-card'
    data-testid='running-job-card'
    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
  >
    <Spinner animation='border' size='sm' style={{ flexShrink: 0, color: INK[400] }} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {job.dataSourceName || `Data source ${job.dataSourceId}`}
      </div>
      <div style={{ fontSize: 11, color: INK[400], marginTop: 1 }}>
        {job.status === 'PENDING' ? 'Fingerprint queued…' : 'Fingerprinting…'}
      </div>
    </div>
    <button
      type='button'
      className='metadata-sortbtn'
      data-testid='running-job-cancel'
      onClick={() => onCancel && onCancel(job.id)}
      disabled={!!cancelling || !onCancel}
    >
      {cancelling ? 'Cancelling…' : 'Cancel'}
    </button>
  </div>
);

const SchemaIdentityList = ({
  identities,
  sort,
  selectedId,
  onSelect,
  loading,
  activeJobs,
  onCancelJob,
  cancelling,
}) => {
  const maxSizeGB = useMemo(() => {
    if (!identities || identities.length === 0) return 0;
    return Math.max(...identities.map((d) => d.sizeGB || 0));
  }, [identities]);

  const sorted = useMemo(
    () => (identities ? sortIdentities(identities, sort) : []),
    [identities, sort]
  );

  // Attach each active job to the identity that contains its data source; jobs for
  // sources with no identity yet fall through to standalone cards at the top.
  const { jobsByIdentityId, orphanJobs } = useMemo(() => {
    const jobs = activeJobs || [];
    const byIdentity = {};
    const orphans = [];
    jobs.forEach((j) => {
      const owner = (identities || []).find((identity) =>
        (identity.dataSources || []).some(
          (d) => String(d.dataSourceId) === String(j.dataSourceId)
        )
      );
      if (owner) {
        (byIdentity[owner.id] = byIdentity[owner.id] || []).push(j);
      } else {
        orphans.push(j);
      }
    });
    return { jobsByIdentityId: byIdentity, orphanJobs: orphans };
  }, [activeJobs, identities]);

  const orphanCards = orphanJobs.map((j) => (
    <RunningJobCard
      key={j.id}
      job={j}
      onCancel={onCancelJob}
      cancelling={cancelling && cancelling[j.id]}
    />
  ));

  if (loading) {
    return (
      <div className='metadata-left'>
        {orphanCards}
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={92} />
        ))}
      </div>
    );
  }

  if (!identities || identities.length === 0) {
    return (
      <div className='metadata-left'>
        {orphanCards}
        {orphanJobs.length === 0 && (
          <div className='metadata-empty'>No schema identities yet.</div>
        )}
      </div>
    );
  }

  return (
    <div className='metadata-left'>
      {orphanCards}
      {sorted.map((identity) => (
        <SchemaIdentityCard
          key={identity.id}
          identity={identity}
          selected={String(identity.id) === String(selectedId)}
          maxSizeGB={maxSizeGB}
          onSelect={onSelect}
          runningJobs={jobsByIdentityId[identity.id]}
          onCancelJob={onCancelJob}
          cancelling={cancelling}
        />
      ))}
    </div>
  );
};

export default SchemaIdentityList;
